import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import * as caseService from '../services/caseService';
import * as exportService from '../services/exportService';
import PageHeader from './Navigation/PageHeader';
import { getActionGuidance, getCaseTypeGuidance } from '../data/actionGuidance';
import FileUpload from './common/FileUpload';
import DocumentUploadForm from './common/DocumentUploadForm';
import useMediaQuery from '../hooks/useMediaQuery';
import CaseTimeline from './CaseTimeline';
import { ExportModal } from './Export';
import CustomDatePicker from './common/CustomDatePicker';
import usePersistedTab from '../hooks/usePersistedTab';

const {
  FiArrowLeft,
  FiUser,
  FiCalendar,
  FiFileText,
  FiClock,
  FiCheck,
  FiAlertTriangle,
  FiEdit3,
  FiPlus,
  FiDownload,
  FiUpload,
  FiSave,
  FiX,
  FiPlay,
  FiPause,
  FiCheckCircle,
  FiFolder,
  FiMessageSquare,
  FiTarget,
  FiShield,
  FiUsers,
  FiSettings,
  FiChevronLeft,
  FiChevronRight,
  FiEdit,
  FiTrash2,
  FiExternalLink,
  FiChevronDown,
  FiChevronUp,
  FiInfo,
  FiLightbulb,
  FiAlertCircle,
  FiActivity
} = FiIcons;

const CaseDetails = () => {
  const { caseId } = useParams();
  const [caseData, setCaseData] = useState(null);
  const [activeTab, setActiveTab] = usePersistedTab('overview');
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [showActionPopup, setShowActionPopup] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [documentContext, setDocumentContext] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  // Responsive breakpoints
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
  const isMobile = useMediaQuery('(max-width: 767px)');

  // Pagination state for audit log
  const [auditCurrentPage, setAuditCurrentPage] = useState(1);
  const [auditEntriesPerPage] = useState(10);

  useEffect(() => {
    loadCaseData();
    loadTimelineData();
  }, [caseId]);

  const loadCaseData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await caseService.getCaseById(caseId);

      // Calculate progress
      const progress = calculateProgress(data.case_stages || []);
      setCaseData({ ...data, progress });
    } catch (err) {
      console.error('Error loading case:', err);
      setError('Failed to load case details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadTimelineData = async () => {
    try {
      setTimelineLoading(true);
      const timeline = await caseService.getCaseTimeline(caseId);
      setTimelineEvents(timeline);
    } catch (err) {
      console.error('Error loading timeline:', err);
      // Don't set main error, timeline is optional
    } finally {
      setTimelineLoading(false);
    }
  };

  const calculateProgress = (stages) => {
    let totalActions = 0;
    let completedActions = 0;

    stages.forEach(stage => {
      const actions = stage.case_actions || [];
      totalActions += actions.length;
      completedActions += actions.filter(action => action.is_completed === true).length;
    });

    return {
      completedActions,
      totalActions,
      completionPercentage: totalActions > 0 ? Math.round((completedActions / totalActions) * 100) : 0
    };
  };

  const updateActionFromPopup = async (updatedActionData) => {
    try {
      setLoading(true);
      setError(null);

      const { stageId, action: updatedAction } = updatedActionData;

      // Update the action in Supabase (only valid columns)
      await caseService.updateCaseAction(updatedAction.id, {
        is_completed: updatedAction.completed,
        notes: updatedAction.notes,
        actual_action_date: updatedAction.completedDate || null  // NEW: Support backdating
      });

      // Add audit entry
      const stageName = caseData.case_stages.find(s => s.id === stageId)?.stage_name || 'Unknown stage';
      const actionName = updatedAction.action_name || 'Unknown action';
      await caseService.addCaseAuditEntry(
        caseData.id,
        updatedAction.completed ? 'Action Completed' : 'Action Updated',
        `${updatedAction.completed ? 'Completed' : 'Updated'} action: ${actionName} in ${stageName}`
      );

      // Reload case data and timeline to get updated progress
      await loadCaseData();
      await loadTimelineData();
      setShowActionPopup(false);
      setSelectedAction(null);
    } catch (err) {
      console.error('Error updating action:', err);
      setError('Failed to update action. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const addNote = async (noteData) => {
    try {
      setLoading(true);
      setError(null);

      await caseService.addCaseNote(caseData.id, {
        title: noteData.title,
        content: noteData.content,
        type: noteData.type || 'general',
        actual_note_date: noteData.actualNoteDate ? new Date(noteData.actualNoteDate).toISOString() : null  // NEW: Support backdating
      });

      await loadCaseData();
      setShowNoteForm(false);
    } catch (err) {
      console.error('Error adding note:', err);
      setError('Failed to add note. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await caseService.deleteCaseNote(noteId);

      await loadCaseData();
    } catch (err) {
      console.error('Error deleting note:', err);
      setError('Failed to delete note. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const addDocument = async (file, documentData) => {
    try {
      setUploading(true);
      setError(null);

      // Upload file and save to database
      await caseService.uploadCaseDocument(caseData.id, file, documentData);

      // Reload case data and timeline
      await loadCaseData();
      await loadTimelineData();

      // Close upload UI (modal, tab, or inline)
      setShowDocumentUpload(false);
      setDocumentContext(null);

      // Switch back to documents tab if on upload tab
      if (activeTab === 'upload') {
        setActiveTab('documents');
      }
    } catch (err) {
      console.error('Error uploading document:', err);
      setError('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const openDocumentUploadForAction = (stageId, actionId) => {
    // This function is now handled within ActionDetailModal
    // Kept for backward compatibility but does nothing
    // The modal handles upload internally with tabs/inline
  };

  const deleteDocument = async (documentId, filePath) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await caseService.deleteCaseDocument(documentId, filePath);

      // Reload case data
      await loadCaseData();
    } catch (err) {
      console.error('Error deleting document:', err);
      setError('Failed to delete document. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const downloadDocument = async (doc) => {
    try {
      if (doc.signedUrl) {
        // Open signed URL in new tab
        window.open(doc.signedUrl, '_blank');
      } else {
        setError('Document URL not available. Please try again.');
      }
    } catch (err) {
      console.error('Error downloading document:', err);
      setError('Failed to download document. Please try again.');
    }
  };

  const handleExport = async (exportType, options) => {
    try {
      setExportLoading(true);
      setError(null);

      await exportService.exportDocument('case', caseData.id, exportType, options);

      // Reload case data to get updated audit log
      await loadCaseData();
    } catch (err) {
      console.error('Error exporting case:', err);
      setError('Failed to export case. Please try again.');
      throw err; // Re-throw so modal can handle it
    } finally {
      setExportLoading(false);
    }
  };

  const handleEditCase = async (updates) => {
    try {
      setEditSaving(true);
      setError(null);

      await caseService.updateCase(caseData.id, updates);

      // Add audit entry
      await caseService.addCaseAuditEntry(
        caseData.id,
        'Case Updated',
        `Updated case details: ${Object.keys(updates).join(', ')}`
      );

      // Reload case data and timeline
      await loadCaseData();
      await loadTimelineData();
      setShowEditModal(false);
    } catch (err) {
      console.error('Error updating case:', err);
      setError('Failed to update case. Please try again.');
    } finally {
      setEditSaving(false);
    }
  };

  const getStageStatus = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getStageIcon = (status) => {
    switch (status) {
      case 'completed': return FiCheckCircle;
      case 'active': return FiPlay;
      case 'pending': return FiClock;
      default: return FiClock;
    }
  };

  // Pagination logic for audit log
  const getPaginatedAuditEntries = () => {
    const auditLog = caseData?.case_audit_log || [];
    const startIndex = (auditCurrentPage - 1) * auditEntriesPerPage;
    const endIndex = startIndex + auditEntriesPerPage;
    return auditLog.slice(startIndex, endIndex);
  };

  const getTotalAuditPages = () => {
    const auditLog = caseData?.case_audit_log || [];
    return Math.ceil(auditLog.length / auditEntriesPerPage);
  };

  const handleAuditPageChange = (newPage) => {
    setAuditCurrentPage(newPage);
  };

  if (loading && !caseData) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading case details...</p>
        </div>
      </div>
    );
  }

  if (error && !caseData) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <SafeIcon icon={FiAlertTriangle} className="text-red-600 text-4xl mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Case</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <Link to="/cases" className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700">
            Back to Cases
          </Link>
        </div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center py-12">
          <SafeIcon icon={FiAlertTriangle} className="text-6xl text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Case Not Found</h3>
          <p className="text-gray-600 mb-4">The requested case could not be found.</p>
          <Link to="/cases" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
            Back to Cases
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Link to="/cases" className="mr-4">
            <SafeIcon icon={FiArrowLeft} className="text-2xl text-gray-600 hover:text-gray-900" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{caseData.title}</h1>
            <p className="text-gray-600">{caseData.case_id} • {caseData.staff?.full_name}</p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowEditModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <SafeIcon icon={FiEdit} />
            <span>Edit</span>
          </button>
          <button
            onClick={() => setShowExportModal(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
          >
            <SafeIcon icon={FiDownload} />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3"
        >
          <SafeIcon icon={FiAlertTriangle} className="text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </motion.div>
      )}

      {/* Enhanced Case Summary */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid md:grid-cols-5 gap-6">
          <div>
            <h3 className="text-sm font-medium text-gray-500">Employee</h3>
            <p className="mt-1 text-lg font-semibold">{caseData.staff?.full_name}</p>
            <p className="text-sm text-gray-600">{caseData.staff?.department}</p>
            <p className="text-xs text-gray-500">{caseData.staff?.position}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Case Type</h3>
            <p className="mt-1 text-lg font-semibold capitalize">{caseData.case_type.replace('-', ' ')}</p>
            <p className="text-xs text-gray-500">{caseData.case_id}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Status</h3>
            <span
              className={`mt-1 inline-block px-2 py-1 rounded text-sm font-medium ${caseData.status === 'active'
                ? 'bg-blue-100 text-blue-800'
                : caseData.status === 'completed'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
                }`}
            >
              {caseData.status.toUpperCase()}
            </span>
            <p className="text-xs text-gray-500 mt-1">
              Priority:{' '}
              <span
                className={`font-medium ${caseData.priority === 'high'
                  ? 'text-red-600'
                  : caseData.priority === 'medium'
                    ? 'text-yellow-600'
                    : 'text-green-600'
                  }`}
              >
                {caseData.priority?.toUpperCase()}
              </span>
            </p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Action Progress</h3>
            <div className="mt-1">
              <div className="flex justify-between text-sm mb-1">
                <span>
                  {caseData.progress?.completedActions || 0}/{caseData.progress?.totalActions || 0} actions
                </span>
                <span>{caseData.progress?.completionPercentage || 0}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${caseData.progress?.completionPercentage || 0}%` }}
                />
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Stage Progress</h3>
            <div className="mt-1">
              <div className="flex justify-between text-sm mb-1">
                <span>
                  {caseData.case_stages.filter(s => s.status === 'completed').length}/{caseData.case_stages.length}{' '}
                  stages
                </span>
                <span>
                  {Math.round(
                    (caseData.case_stages.filter(s => s.status === 'completed').length / caseData.case_stages.length) *
                    100
                  )}
                  %
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all duration-500"
                  style={{
                    width: `${(caseData.case_stages.filter(s => s.status === 'completed').length /
                      caseData.case_stages.length) *
                      100
                      }%`
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="border-b border-gray-200">
          {/* Mobile Dropdown */}
          <div className="md:hidden p-4">
            <label htmlFor="tabs" className="sr-only">Select a tab</label>
            <select
              id="tabs"
              name="tabs"
              className="block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 py-2 px-3"
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
            >
              {[
                { id: 'overview', name: 'Overview' },
                { id: 'actions', name: 'Actions & Progress' },
                { id: 'stages', name: 'Workflow Stages' },
                { id: 'notes', name: 'Notes' },
                { id: 'documents', name: 'Documents' },
                { id: 'timeline', name: 'Timeline' },
                { id: 'audit', name: 'Audit Log' },
                ...(isDesktop || isTablet ? [{ id: 'upload', name: 'Upload Document' }] : [])
              ].map((tab) => (
                <option key={tab.id} value={tab.id}>{tab.name}</option>
              ))}
            </select>
          </div>

          {/* Desktop Tabs */}
          <nav className="hidden md:flex space-x-8 px-6 overflow-x-auto">
            {[
              { id: 'overview', name: 'Overview', icon: FiFolder },
              { id: 'actions', name: 'Actions & Progress', icon: FiTarget },
              { id: 'stages', name: 'Workflow Stages', icon: FiCheckCircle },
              { id: 'notes', name: 'Notes', icon: FiMessageSquare },
              { id: 'documents', name: 'Documents', icon: FiFileText },
              { id: 'timeline', name: 'Timeline', icon: FiActivity },
              { id: 'audit', name: 'Audit Log', icon: FiClock },
              ...(isDesktop || isTablet ? [{ id: 'upload', name: 'Upload Document', icon: FiUpload }] : [])
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                <SafeIcon icon={tab.icon} />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Case-Level Guidance */}
              {(() => {
                const caseGuidance = getCaseTypeGuidance(caseData.case_type);
                if (!caseGuidance) return null;

                return (
                  <div className="border-2 border-purple-200 rounded-lg overflow-hidden bg-gradient-to-r from-purple-50 to-pink-50">
                    <div className="px-4 py-3 bg-purple-100 border-b border-purple-200">
                      <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiShield} className="w-5 h-5 text-purple-600" />
                        <h3 className="font-semibold text-purple-900">{caseGuidance.title}</h3>
                      </div>
                    </div>

                    <div className="px-4 py-4 space-y-4">
                      {/* Importance */}
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <div className="flex items-center space-x-2 mb-2">
                          <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-red-600" />
                          <h4 className="font-semibold text-red-900 text-sm">⚠️ Why This Matters</h4>
                        </div>
                        <p className="text-sm text-red-800">{caseGuidance.importance}</p>
                      </div>

                      {/* What to Look For */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-center space-x-2 mb-2">
                          <SafeIcon icon={FiTarget} className="w-4 h-4 text-blue-600" />
                          <h4 className="font-semibold text-blue-900 text-sm">🎯 What to Look For</h4>
                        </div>
                        <ul className="space-y-1 text-sm text-blue-800">
                          {caseGuidance.whatToLookFor.map((item, idx) => (
                            <li key={idx} className="flex items-start space-x-2">
                              <span className="text-blue-600 mt-0.5">•</span>
                              <span className="whitespace-pre-line">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Best Practices */}
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-center space-x-2 mb-2">
                          <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-600" />
                          <h4 className="font-semibold text-green-900 text-sm">✅ Best Practices</h4>
                        </div>
                        <ul className="space-y-1 text-sm text-green-800">
                          {caseGuidance.bestPractices.map((item, idx) => (
                            <li key={idx} className="flex items-start space-x-2">
                              <span className="text-green-600 mt-0.5">•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Common Pitfalls */}
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <div className="flex items-center space-x-2 mb-2">
                          <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 text-amber-600" />
                          <h4 className="font-semibold text-amber-900 text-sm">⚡ Common Pitfalls</h4>
                        </div>
                        <ul className="space-y-1 text-sm text-amber-800">
                          {caseGuidance.commonPitfalls.map((item, idx) => (
                            <li key={idx} className="flex items-start space-x-2">
                              <span className="text-amber-600 mt-0.5">!</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Key Legal Points */}
                      <div className="bg-gray-50 border border-gray-300 rounded-lg p-3">
                        <div className="flex items-center space-x-2 mb-2">
                          <SafeIcon icon={FiShield} className="w-4 h-4 text-gray-700" />
                          <h4 className="font-semibold text-gray-900 text-sm">⚖️ Key Legal Points</h4>
                        </div>
                        <ul className="space-y-1 text-sm text-gray-800">
                          {caseGuidance.keyLegalPoints.map((item, idx) => (
                            <li key={idx} className="flex items-start space-x-2">
                              <span className="text-gray-600 mt-0.5">§</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div>
                <h3 className="text-lg font-semibold mb-3">Case Description</h3>
                <p className="text-gray-700">{caseData.description || 'No description provided.'}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Case Information</h3>
                  <dl className="space-y-2">
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-500">Created:</dt>
                      <dd className="text-sm font-medium">{new Date(caseData.created_at).toLocaleString()}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-500">Last Updated:</dt>
                      <dd className="text-sm font-medium">{new Date(caseData.updated_at).toLocaleString()}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-500">Created By:</dt>
                      <dd className="text-sm font-medium">{caseData.created_by}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-500">Total Actions:</dt>
                      <dd className="text-sm font-medium">{caseData.progress?.totalActions || 0}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-500">Completed Actions:</dt>
                      <dd className="text-sm font-medium text-green-600">{caseData.progress?.completedActions || 0}</dd>
                    </div>
                  </dl>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Current Stage</h3>
                  {(() => {
                    // Find active stage first, then fall back to last completed stage
                    const activeStage = caseData.case_stages.find(s => s.status === 'active');
                    const completedStages = caseData.case_stages.filter(s => s.status === 'completed');
                    const lastCompletedStage = completedStages.length > 0
                      ? completedStages[completedStages.length - 1]
                      : null;
                    const currentStage = activeStage || lastCompletedStage;
                    const isCompleted = !activeStage && lastCompletedStage;

                    return currentStage ? (
                      <div className={`${isCompleted ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'} border rounded-lg p-4`}>
                        <div className="flex items-center space-x-2 mb-2">
                          <SafeIcon icon={isCompleted ? FiCheckCircle : FiPlay} className={isCompleted ? 'text-green-600' : 'text-blue-600'} />
                          <span className={`font-medium ${isCompleted ? 'text-green-900' : 'text-blue-900'}`}>
                            {currentStage.stage_name}
                          </span>
                          {isCompleted && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Completed</span>
                          )}
                        </div>
                        {currentStage.timeline && (
                          <p className={`text-sm ${isCompleted ? 'text-green-700' : 'text-blue-700'}`}>
                            Timeline: {currentStage.timeline}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center space-x-2">
                          <SafeIcon icon={FiClock} className="text-gray-400" />
                          <span className="font-medium text-gray-500">No stages yet</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Actions & Progress Tab */}
          {activeTab === 'actions' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">Detailed Action Progress</h3>
                <div className="text-sm text-gray-600">
                  {caseData.progress?.completedActions || 0} of {caseData.progress?.totalActions || 0} actions completed
                </div>
              </div>

              <div className="space-y-6">
                {caseData.case_stages.map((stage, stageIndex) => (
                  <motion.div
                    key={stage.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: stageIndex * 0.1 }}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <div
                      className={`px-6 py-4 ${stage.status === 'completed'
                        ? 'bg-green-50 border-green-200'
                        : stage.status === 'active'
                          ? 'bg-blue-50 border-blue-200'
                          : 'bg-gray-50 border-gray-200'
                        }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <SafeIcon
                            icon={getStageIcon(stage.status)}
                            className={`text-xl ${stage.status === 'completed'
                              ? 'text-green-600'
                              : stage.status === 'active'
                                ? 'text-blue-600'
                                : 'text-gray-400'
                              }`}
                          />
                          <div>
                            <h4 className="font-medium text-gray-900">{stage.stage_name}</h4>
                            <p className="text-sm text-gray-600">
                              {stage.case_actions.filter(a => a.is_completed === true).length}/{stage.case_actions.length}{' '}
                              actions completed
                              {stage.timeline && ` • Timeline: ${stage.timeline}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getStageStatus(stage.status)}`}>
                            {stage.status.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="space-y-3">
                        {stage.case_actions.map((action, actionIndex) => (
                          <div
                            key={action.id}
                            onClick={() => {
                              setSelectedAction({
                                stageId: stage.id,
                                action: {
                                  ...action,
                                  completed: action.is_completed,
                                  completedDate: action.actual_action_date || action.completed_at,
                                  name: action.action_name,
                                  legal: action.is_legal_critical,
                                  acas: action.is_acas_required
                                },
                                stageName: stage.stage_name,
                                caseType: caseData.case_type
                              });
                              setShowActionPopup(true);
                            }}
                            className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-all ${action.is_completed === true
                              ? 'bg-green-50 border-green-200 hover:bg-green-100'
                              : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-blue-300'
                              }`}
                          >
                            <div
                              className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${action.is_completed === true
                                ? 'bg-green-600 border-green-600 text-white'
                                : 'border-gray-300 hover:border-blue-500'
                                }`}
                            >
                              {action.is_completed === true && <SafeIcon icon={FiCheck} className="w-3 h-3" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <p
                                    className={`text-sm font-medium ${action.is_completed === true ? 'text-green-800' : 'text-gray-900'
                                      }`}
                                  >
                                    {action.action_name}
                                  </p>
                                  {action.notes && (
                                    <p className="text-xs text-gray-600 mt-1 whitespace-pre-line">Note: {action.notes}</p>
                                  )}
                                  {(action.actual_action_date || action.completed_at) && (
                                    <p className="text-xs text-green-600 mt-1">
                                      Completed: {new Date(action.actual_action_date || action.completed_at).toLocaleString()}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center space-x-2 ml-2">
                                  {(caseData.case_documents || []).filter(d => d.action_id === action.id).length > 0 && (
                                    <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                                      {(caseData.case_documents || []).filter(d => d.action_id === action.id).length} docs
                                    </span>
                                  )}
                                  {action.is_legal_critical && (
                                    <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded">
                                      ⚖️ Legal
                                    </span>
                                  )}
                                  {action.is_acas_required && (
                                    <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded">
                                      🟢 ACAS
                                    </span>
                                  )}
                                  <SafeIcon icon={FiEdit} className="w-4 h-4 text-gray-400" />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Stages Tab */}
          {activeTab === 'stages' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Workflow Stages Overview</h3>
              </div>

              <div className="space-y-4">
                {caseData.case_stages.map((stage, index) => (
                  <motion.div
                    key={stage.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <SafeIcon
                          icon={getStageIcon(stage.status)}
                          className={`text-xl ${stage.status === 'completed'
                            ? 'text-green-600'
                            : stage.status === 'active'
                              ? 'text-blue-600'
                              : 'text-gray-400'
                            }`}
                        />
                        <div>
                          <h4 className="font-medium text-gray-900">{stage.stage_name}</h4>
                          <p className="text-sm text-gray-600">
                            {stage.case_actions.length} actions • {stage.timeline}
                          </p>
                          {stage.completed_at && (
                            <p className="text-sm text-gray-500">
                              Completed: {new Date(stage.completed_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="text-right text-sm">
                          <div className="text-gray-600">
                            {stage.case_actions.filter(a => a.is_completed === true).length}/{stage.case_actions.length}
                          </div>
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${stage.status === 'completed'
                                ? 'bg-green-600'
                                : stage.status === 'active'
                                  ? 'bg-blue-600'
                                  : 'bg-gray-400'
                                }`}
                              style={{
                                width: `${(stage.case_actions.filter(a => a.is_completed === true).length /
                                  stage.case_actions.length) *
                                  100
                                  }%`
                              }}
                            />
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStageStatus(stage.status)}`}>
                          {stage.status.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Case Notes</h3>
                <button
                  onClick={() => setShowNoteForm(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                >
                  <SafeIcon icon={FiPlus} />
                  <span>Add Note</span>
                </button>
              </div>

              <div className="space-y-4">
                {(caseData.case_notes || []).map(note => (
                  <div key={note.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-gray-900">{note.note_title}</h4>
                      <span className="text-xs text-gray-500">{new Date(note.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-gray-700 mb-2">{note.note_content}</p>
                    <div className="flex items-center space-x-2 text-xs text-gray-500">
                      <span>By: {note.created_by}</span>
                      <span>• Type: {note.note_type}</span>
                    </div>
                    <div className="mt-2 flex justify-end">
                      <button
                        onClick={() => deleteNote(note.id)}
                        className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 flex items-center space-x-1 text-xs"
                        title="Delete Note"
                      >
                        <SafeIcon icon={FiTrash2} className="w-3 h-3" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                ))}

                {(!caseData.case_notes || caseData.case_notes.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    No notes added yet. Click "Add Note" to get started.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Documents Tab - WITH REAL FILE UPLOAD */}
          {activeTab === 'documents' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Case Documents</h3>
                <button
                  onClick={() => setShowDocumentUpload(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                >
                  <SafeIcon icon={FiUpload} />
                  <span>Upload Document</span>
                </button>
              </div>

              <div className="space-y-4">
                {(caseData.case_documents || []).map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiFileText} className="text-blue-600 text-xl" />
                      <div>
                        <h4 className="font-medium text-gray-900">{doc.document_name}</h4>
                        <p className="text-sm text-gray-500">
                          {doc.document_type} • Uploaded {new Date(doc.uploaded_at).toLocaleString()}
                        </p>
                        {doc.file_size && (
                          <p className="text-xs text-gray-500">
                            Size: {(doc.file_size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        )}
                        {doc.description && <p className="text-sm text-gray-600 mt-1">{doc.description}</p>}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => downloadDocument(doc)}
                        className="text-blue-600 hover:text-blue-800 p-2 rounded hover:bg-blue-50"
                        title="Download Document"
                      >
                        <SafeIcon icon={FiDownload} />
                      </button>
                      {doc.signedUrl && (
                        <button
                          onClick={() => window.open(doc.signedUrl, '_blank')}
                          className="text-gray-600 hover:text-gray-800 p-2 rounded hover:bg-gray-50"
                          title="Open in New Tab"
                        >
                          <SafeIcon icon={FiExternalLink} />
                        </button>
                      )}
                      <button
                        onClick={() => deleteDocument(doc.id, doc.file_path)}
                        className="text-red-600 hover:text-red-800 p-2 rounded hover:bg-red-50"
                        title="Delete Document"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  </div>
                ))}

                {(!caseData.case_documents || caseData.case_documents.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    No documents uploaded yet. Click "Upload Document" to get started.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Case Timeline</h3>
                <p className="text-sm text-gray-600">
                  Chronological view of all case events based on actual completion dates
                </p>
              </div>

              <CaseTimeline events={timelineEvents} loading={timelineLoading} />
            </div>
          )}

          {/* Audit Log Tab with Pagination */}
          {activeTab === 'audit' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Audit Trail</h3>
                <div className="text-sm text-gray-600">{(caseData.case_audit_log || []).length} total entries</div>
              </div>

              <div className="space-y-4">
                {getPaginatedAuditEntries().map(entry => (
                  <div key={entry.id} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium text-gray-900">{entry.action}</h4>
                          <p className="text-gray-700 mt-1">{entry.details}</p>
                        </div>
                        <div className="text-right text-sm text-gray-500">
                          <p>{new Date(entry.performed_at).toLocaleString()}</p>
                          <p>By: {entry.performed_by}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {(!caseData.case_audit_log || caseData.case_audit_log.length === 0) && (
                  <div className="text-center py-8 text-gray-500">No audit entries yet.</div>
                )}
              </div>

              {/* Pagination Controls */}
              {getTotalAuditPages() > 1 && (
                <div className="flex items-center justify-between mt-6 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">
                      Showing {(auditCurrentPage - 1) * auditEntriesPerPage + 1} to{' '}
                      {Math.min(auditCurrentPage * auditEntriesPerPage, (caseData.case_audit_log || []).length)} of{' '}
                      {(caseData.case_audit_log || []).length} entries
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleAuditPageChange(auditCurrentPage - 1)}
                      disabled={auditCurrentPage === 1}
                      className={`p-2 rounded-lg border ${auditCurrentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                        }`}
                    >
                      <SafeIcon icon={FiChevronLeft} className="w-4 h-4" />
                    </button>
                    <div className="flex space-x-1">
                      {Array.from({ length: getTotalAuditPages() }, (_, i) => i + 1).map(pageNum => (
                        <button
                          key={pageNum}
                          onClick={() => handleAuditPageChange(pageNum)}
                          className={`px-3 py-2 text-sm rounded-lg border ${pageNum === auditCurrentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                            }`}
                        >
                          {pageNum}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => handleAuditPageChange(auditCurrentPage + 1)}
                      disabled={auditCurrentPage === getTotalAuditPages()}
                      className={`p-2 rounded-lg border ${auditCurrentPage === getTotalAuditPages()
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                        }`}
                    >
                      <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Upload Tab - Desktop/Tablet Only */}
          {activeTab === 'upload' && (isDesktop || isTablet) && (
            <div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Document</h3>
                {documentContext?.actionId && (
                  <p className="text-sm text-gray-600">
                    Uploading document for specific action
                  </p>
                )}
                {!documentContext?.actionId && (
                  <p className="text-sm text-gray-600">
                    Uploading general case document
                  </p>
                )}
              </div>

              <div className="max-w-2xl">
                <DocumentUploadForm
                  onSubmit={addDocument}
                  onCancel={() => {
                    setActiveTab('documents');
                    setDocumentContext(null);
                  }}
                  uploading={uploading}
                  stageId={documentContext?.stageId}
                  actionId={documentContext?.actionId}
                  compact={false}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showNoteForm && <NoteFormModal onSubmit={addNote} onCancel={() => setShowNoteForm(false)} />}

      {showDocumentUpload && (
        <DocumentUploadModal
          onSubmit={addDocument}
          onCancel={() => {
            setShowDocumentUpload(false);
            setDocumentContext(null);
          }}
          stageId={documentContext?.stageId}
          actionId={documentContext?.actionId}
        />
      )}

      {showActionPopup && selectedAction && (
        <ActionDetailModal
          actionData={selectedAction}
          caseId={caseData.id}
          onSubmit={updateActionFromPopup}
          onDocumentUpload={addDocument}
          onUploadDocument={(stageId, actionId) => openDocumentUploadForAction(stageId, actionId)}
          documents={(caseData.case_documents || []).filter(d => d.action_id === selectedAction.action.id)}
          onCancel={() => {
            setShowActionPopup(false);
            setSelectedAction(null);
          }}
        />
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        entityType="case"
        entityName={`${caseData.case_id} - ${caseData.title}`}
        isLoading={exportLoading}
        documents={caseData.case_documents || []}
      />

      {/* Edit Case Modal */}
      {showEditModal && (
        <EditCaseModal
          caseData={caseData}
          onSubmit={handleEditCase}
          onCancel={() => setShowEditModal(false)}
          saving={editSaving}
        />
      )}
    </div>
  );
};

// Action Detail Modal (keeping existing implementation with Supabase field mapping)
const ActionDetailModal = ({ actionData, caseId, documents = [], onSubmit, onCancel, onDocumentUpload, onUploadDocument }) => {
  const [formData, setFormData] = useState({
    completed: actionData.action.completed === true,
    completedDate: actionData.action.completedDate || '',
    notes: actionData.action.notes || '',
    documents: actionData.action.documents || []
  });

  const [activeModalTab, setActiveModalTab] = useState('guidance');
  const [showInlineUpload, setShowInlineUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isGuidanceExpanded, setIsGuidanceExpanded] = useState(false); // Default collapsed on mobile

  // Responsive breakpoints
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
  const isMobile = useMediaQuery('(max-width: 767px)');


  useEffect(() => {
    if (formData.completed && !formData.completedDate) {
      setFormData(prev => ({
        ...prev,
        completedDate: new Date().toISOString().slice(0, 16)
      }));
    }
  }, [formData.completed]);

  const handleSubmit = e => {
    e.preventDefault();

    // Convert datetime-local to ISO string properly
    // datetime-local gives us "2024-11-15T14:30" but we need full ISO "2024-11-15T14:30:00.000Z"
    let completedDateISO = null;
    if (formData.completed && formData.completedDate) {
      // Parse the datetime-local value and convert to ISO
      completedDateISO = new Date(formData.completedDate).toISOString();
    } else if (formData.completed) {
      completedDateISO = new Date().toISOString();
    }

    const updatedAction = {
      ...actionData.action,
      completed: formData.completed,
      completedDate: completedDateISO,
      notes: formData.notes,
      documents: formData.documents
    };

    setSubmitting(true);
    // We need to await this to show the loading state properly
    // The parent's onSubmit is async (updateActionFromPopup)
    Promise.resolve(onSubmit({
      stageId: actionData.stageId,
      action: updatedAction
    })).finally(() => {
      setSubmitting(false);
    });
  };

  const handleUploadClick = () => {
    if (isDesktop || isTablet) {
      setActiveModalTab('upload');
    } else {
      setShowInlineUpload(!showInlineUpload);
    }
  };

  const handleDocumentUpload = async (file, documentData) => {
    try {
      // Convert datetime-local to ISO string for the action date
      let completedDateISO = null;
      if (formData.completed && formData.completedDate) {
        completedDateISO = new Date(formData.completedDate).toISOString();
      } else if (formData.completed) {
        completedDateISO = new Date().toISOString();
      }

      // Auto-save the action first (silently) so the document inherits the correct date
      // This ensures the action's completion date is saved before the document is uploaded
      const updatedAction = {
        ...actionData.action,
        completed: formData.completed,
        completedDate: completedDateISO,
        notes: formData.notes,
        documents: formData.documents
      };

      await onSubmit({
        stageId: actionData.stageId,
        action: updatedAction
      });

      // Use the action's completion date for the document
      const enrichedDocumentData = {
        ...documentData,
        actual_document_date: documentData.actual_document_date || completedDateISO || null
      };

      // Call parent's upload function with file and enriched document data
      await onDocumentUpload(file, enrichedDocumentData);

      // Close upload UI after successful upload
      setShowInlineUpload(false);
      if (activeModalTab === 'upload') {
        setActiveModalTab('details');
      }
    } catch (error) {
      console.error('Error uploading document in modal:', error);
      // Error is already handled by parent's addDocument function
    }
  };



  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Action Details</h2>
              <p className="text-sm text-gray-600 mt-1">Stage: {actionData.stageName}</p>
            </div>
            <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>

          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="flex items-center space-x-2">
                {actionData.action.legal && (
                  <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded">⚖️ Legal</span>
                )}
                {actionData.action.acas && (
                  <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded">🟢 ACAS</span>
                )}
              </div>
            </div>
            <h3 className="font-medium text-gray-900 mt-2">{actionData.action.name}</h3>
          </div>

          {/* Mobile Guidance - Collapsible */}
          {isMobile && (() => {
            const guidance = getActionGuidance(actionData.caseType, actionData.action.name);
            if (!guidance) return null;

            return (
              <div className="mb-4 border-2 border-blue-200 rounded-lg overflow-hidden">
                {/* Collapsible Header */}
                <button
                  onClick={() => setIsGuidanceExpanded(!isGuidanceExpanded)}
                  className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50"
                >
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiLightbulb} className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-blue-900">Guidance & Tips</h4>
                  </div>
                  <SafeIcon
                    icon={isGuidanceExpanded ? FiChevronUp : FiChevronDown}
                    className="w-5 h-5 text-blue-500"
                  />
                </button>

                {/* Collapsible Content */}
                <AnimatePresence>
                  {isGuidanceExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 bg-white border-t border-blue-100">
                        <div className="text-sm text-gray-800 whitespace-pre-line leading-relaxed mb-3">
                          {guidance.guidance}
                        </div>

                        {guidance.tips && guidance.tips.length > 0 && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                            <h5 className="font-semibold text-green-900 text-xs mb-2">💡 Tips</h5>
                            <ul className="space-y-1 text-xs text-green-800">
                              {guidance.tips.map((tip, idx) => (
                                <li key={idx} className="flex items-start space-x-1">
                                  <span className="text-green-600">•</span>
                                  <span>{tip}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {guidance.warnings && guidance.warnings.length > 0 && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <h5 className="font-semibold text-amber-900 text-xs mb-2">⚠️ Warnings</h5>
                            <ul className="space-y-1 text-xs text-amber-800">
                              {guidance.warnings.map((warning, idx) => (
                                <li key={idx} className="flex items-start space-x-1">
                                  <span className="text-amber-600">!</span>
                                  <span>{warning}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })()}

          {/* Tab Navigation - Desktop/Tablet Only */}
          {(isDesktop || isTablet) && (
            <div className="border-b border-gray-200 mb-4">
              <nav className="flex space-x-4">
                <button
                  onClick={() => setActiveModalTab('guidance')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeModalTab === 'guidance'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  <SafeIcon icon={FiLightbulb} />
                  <span>Guidance</span>
                </button>
                <button
                  onClick={() => setActiveModalTab('details')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeModalTab === 'details'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  <SafeIcon icon={FiEdit} />
                  <span>Details</span>
                </button>
                <button
                  onClick={() => setActiveModalTab('upload')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeModalTab === 'upload'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  <SafeIcon icon={FiUpload} />
                  <span>Upload Document</span>
                </button>
              </nav>
            </div>
          )}

          {/* Guidance Tab Content - Desktop/Tablet Only (Mobile has its own block above) */}
          {activeModalTab === 'guidance' && !isMobile && (() => {
            const guidance = getActionGuidance(actionData.caseType, actionData.action.name);

            if (!guidance) {
              return (
                <div className="text-center py-12">
                  <SafeIcon icon={FiInfo} className="text-4xl text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Guidance Available</h3>
                  <p className="text-gray-600">No specific guidance is available for this action.</p>
                </div>
              );
            }

            return (
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <SafeIcon icon={FiLightbulb} className="w-6 h-6 text-blue-600" />
                    <h3 className="text-lg font-semibold text-blue-900">Action Guidance & Best Practices</h3>
                  </div>

                  {/* Main Guidance */}
                  <div className="prose prose-sm max-w-none mb-4">
                    <div className="text-gray-800 whitespace-pre-line leading-relaxed">
                      {guidance.guidance}
                    </div>
                  </div>

                  {/* Tips Section */}
                  {guidance.tips && guidance.tips.length > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <SafeIcon icon={FiLightbulb} className="w-5 h-5 text-green-600" />
                        <h4 className="font-semibold text-green-900">💡 Practical Tips</h4>
                      </div>
                      <ul className="space-y-2 text-sm text-green-800">
                        {guidance.tips.map((tip, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <span className="text-green-600 mt-0.5">•</span>
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Warnings Section */}
                  {guidance.warnings && guidance.warnings.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-amber-600" />
                        <h4 className="font-semibold text-amber-900">⚠️ Important Warnings</h4>
                      </div>
                      <ul className="space-y-2 text-sm text-amber-800">
                        {guidance.warnings.map((warning, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <span className="text-amber-600 mt-0.5">!</span>
                            <span>{warning}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Details Tab Content - Always show on mobile, or when tab is active */}
          {(activeModalTab === 'details' || isMobile) && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Completion Status */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="completed"
                    checked={formData.completed}
                    onChange={e => setFormData(prev => ({ ...prev, completed: e.target.checked }))}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="completed" className="text-sm font-medium text-gray-700">
                    Mark as completed
                  </label>
                </div>

                {formData.completed && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Completion Date & Time</label>
                    <CustomDatePicker
                      selected={formData.completedDate ? new Date(formData.completedDate) : null}
                      onChange={(date) => setFormData(prev => ({ ...prev, completedDate: date ? date.toISOString() : '' }))}
                      placeholder="Select date and time"
                      showTimeSelect
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Set the actual date this action was completed. Leave blank for current time.
                    </p>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes & Comments</label>
                  <textarea
                    value={formData.notes}
                    onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows="4"
                    placeholder="Add any relevant notes, observations, or comments..."
                  />
                </div>

                {/* Documents */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="block text-sm font-medium text-gray-700">Attached Documents ({documents.length})</label>
                    <button
                      type="button"
                      onClick={handleUploadClick}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1 ml-3"
                    >
                      <SafeIcon icon={FiUpload} className="w-4 h-4" />
                      <span>{isMobile ? (showInlineUpload ? 'Hide Upload' : 'Upload File') : 'Upload File'}</span>
                    </button>
                  </div>

                  {/* Mobile Inline Upload */}
                  {isMobile && showInlineUpload && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg"
                    >
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Upload Document</h4>
                      <DocumentUploadForm
                        onSubmit={handleDocumentUpload}
                        onCancel={() => setShowInlineUpload(false)}
                        uploading={uploading}
                        stageId={actionData.stageId}
                        actionId={actionData.action.id}
                        compact={true}
                      />
                    </motion.div>
                  )}

                  {/* Document List */}
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {documents.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <SafeIcon icon={FiFileText} className="text-blue-600" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{doc.document_name}</p>
                            <p className="text-xs text-gray-500">
                              {doc.document_type} • Uploaded {new Date(doc.actual_document_date || doc.uploaded_at).toLocaleString()}
                            </p>
                            {doc.file_size && (
                              <p className="text-xs text-gray-500">Size: {(doc.file_size / 1024 / 1024).toFixed(2)} MB</p>
                            )}
                            {doc.description && <p className="text-xs text-gray-600 mt-1">{doc.description}</p>}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {doc.signedUrl && (
                            <button
                              type="button"
                              onClick={() => window.open(doc.signedUrl, '_blank')}
                              className="text-gray-600 hover:text-gray-800 p-2 rounded hover:bg-gray-50"
                              title="Open in New Tab"
                            >
                              <SafeIcon icon={FiExternalLink} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    {documents.length === 0 && (
                      <div className="text-center py-4 text-gray-500 text-sm">No documents linked to this action</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={onCancel}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Upload Tab - Desktop/Tablet Only */}
          {activeModalTab === 'upload' && (isDesktop || isTablet) && (
            <div className="space-y-4">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Document</h3>
                <p className="text-sm text-gray-600">
                  Upload a document for this action: {actionData.action.name}
                </p>
              </div>

              <DocumentUploadForm
                onSubmit={handleDocumentUpload}
                onCancel={() => setActiveModalTab('details')}
                uploading={uploading}
                stageId={actionData.stageId}
                actionId={actionData.action.id}
                compact={false}
              />
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// Note Form Modal (keeping existing implementation)
const NoteFormModal = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'general',
    actualNoteDate: ''  // NEW: For backdating notes
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    if (formData.title && formData.content) {
      setSubmitting(true);
      try {
        await onSubmit(formData);
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Add Case Note</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
            <textarea
              value={formData.content}
              onChange={e => setFormData(prev => ({ ...prev, content: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="4"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={formData.type}
              onChange={e => setFormData(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="general">General</option>
              <option value="compliance">Compliance</option>
              <option value="meeting">Meeting</option>
              <option value="decision">Decision</option>
              <option value="action">Action Required</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note Date (Optional)
            </label>
            <CustomDatePicker
              selected={formData.actualNoteDate ? new Date(formData.actualNoteDate) : null}
              onChange={(date) => setFormData(prev => ({ ...prev, actualNoteDate: date ? date.toISOString() : '' }))}
              placeholder="Select date and time"
              showTimeSelect
            />
            <p className="text-xs text-gray-500 mt-1">
              Set the actual date this note was written. Leave blank for current time.
            </p>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Add Note'
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Document Upload Modal - WITH REAL FILE UPLOAD
const DocumentUploadModal = ({ onSubmit, onCancel, stageId, actionId }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'pdf',
    description: '',
    actualDocumentDate: ''  // NEW: For backdating documents
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();

    if (!selectedFile) {
      alert('Please select a file to upload');
      return;
    }

    setUploading(true);
    try {
      // Convert datetime-local to ISO timestamp
      const documentDataWithDate = {
        ...formData,
        stageId,
        actionId,
        actual_document_date: formData.actualDocumentDate ? new Date(formData.actualDocumentDate).toISOString() : null
      };
      await onSubmit(selectedFile, documentDataWithDate);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Upload Document</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <FileUpload
            onFileSelect={file => {
              setSelectedFile(file);
              if (!formData.name && file) {
                setFormData(prev => ({ ...prev, name: file.name }));
              }
            }}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            maxSize={10 * 1024 * 1024} // 10MB
            label="Select Document"
            description="PDF, Word, or Image files up to 10MB"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Employment Contract, Investigation Report"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
            <select
              value={formData.type}
              onChange={e => setFormData(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="pdf">PDF</option>
              <option value="doc">Word Document</option>
              <option value="email">Email</option>
              <option value="image">Image</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder="Brief description of the document"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Document Date (Optional)
            </label>
            <CustomDatePicker
              selected={formData.actualDocumentDate ? new Date(formData.actualDocumentDate) : null}
              onChange={(date) => setFormData(prev => ({ ...prev, actualDocumentDate: date ? date.toISOString() : '' }))}
              placeholder="Select date and time"
              showTimeSelect
            />
            <p className="text-xs text-gray-500 mt-1">
              Set the actual date this document was created/received. Leave blank for current time.
            </p>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiUpload} />
                  <span>Upload Document</span>
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

/**
 * Edit Case Modal
 * Allows editing of case details including title, description, status, priority, and dates
 */
const EditCaseModal = ({ caseData, onSubmit, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    title: caseData.title || '',
    description: caseData.description || '',
    status: caseData.status || 'active',
    priority: caseData.priority || 'medium',
    actual_created_date: caseData.actual_created_date
      ? new Date(caseData.actual_created_date).toISOString().slice(0, 16)
      : caseData.created_at
        ? new Date(caseData.created_at).toISOString().slice(0, 16)
        : ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    // Build updates object - only include changed fields
    const updates = {};

    if (formData.title !== caseData.title) {
      updates.title = formData.title;
    }
    if (formData.description !== caseData.description) {
      updates.description = formData.description;
    }
    if (formData.status !== caseData.status) {
      updates.status = formData.status;
    }
    if (formData.priority !== caseData.priority) {
      updates.priority = formData.priority;
    }

    // Handle date - convert datetime-local to ISO
    const newActualDate = formData.actual_created_date
      ? new Date(formData.actual_created_date).toISOString()
      : null;
    const currentActualDate = caseData.actual_created_date || caseData.created_at;

    if (newActualDate !== currentActualDate) {
      updates.actual_created_date = newActualDate;
    }

    if (Object.keys(updates).length === 0) {
      onCancel(); // No changes
      return;
    }

    onSubmit(updates);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Edit Case Details</h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600"
              disabled={saving}
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Case Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder="Case description..."
            />
          </div>

          {/* Status and Priority Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="on-hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={e => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          {/* Case Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Case Start Date
            </label>
            <CustomDatePicker
              selected={formData.actual_created_date ? new Date(formData.actual_created_date) : null}
              onChange={(date) => setFormData(prev => ({ ...prev, actual_created_date: date ? date.toISOString() : '' }))}
              placeholder="Select date and time"
              showTimeSelect
            />
            <p className="text-xs text-gray-500 mt-1">
              The actual date this case started. Changing this will update the timeline.
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <SafeIcon icon={FiInfo} className="w-4 h-4 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Timeline Impact</p>
                <p className="text-xs mt-1">
                  Changing the case start date will update the "Case Started" event in the timeline
                  and affect any date-based calculations (e.g., days overdue, tribunal risk alerts).
                </p>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSave} />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CaseDetails;